import { DatePicker } from "antd";
import moment from "moment";
import { useEffect, useState } from "react";
import { useTranslation } from 'react-i18next';
import { getWebMemberCount } from "../../api/statistics.api";
import Button from "../../components/Button";
import ErrorResponse from "../../components/ErrorResponse";
import SimpleHeader from "../../components/layout/SimpleHeader";
import Loader from "../../components/Loader";
import TabItem from "../../components/statistics/TabItem";
import ChartContainer from "../../components/statistics/web/ChartContainer";
import TableContainer from "../../components/statistics/web/TableContainer";
import useAxios from "../../hooks/useAxios";
import useNotification from "../../hooks/useNotification";
import { IGetWebMemberCount } from "../../models/statistics.model";
import MultiCampusModal from "../../components/statistics/regular-test/modals/MultiCampusModal";
import { useSelector } from "react-redux";
import { RootState } from "../../store/rootReducer";
import TableEmpty from "../../components/common/TableEmpty";
import SelectCampusMultiChoice from "../../components/SelectCampusMultiChoice";

enum Contents {
  Table,
  Chart,
}

const StatisticsWeb = () => {
  const { t } = useTranslation();
  const { infoNotification } = useNotification();
  const { auth } = useSelector((state: RootState) => state.auth);
  const { data: res, isLoading, error, execute } = useAxios<IGetWebMemberCount>();

  const [content, setContent] = useState(Contents.Table);
  const [srtDate, setSrtDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [days, setDays] = useState<number>();
  const [date, setDate] = useState('');
  const [data, setData] = useState<any>([]);
  const [campusCodes, setCampusCodes] = useState<string[]>([]);
  const [multiCampusVisible, setMultiCampusVisible] = useState(false);

  useEffect(() => {
    if (res?.data?.length && !isLoading) {
      // @ts-ignore
      const rawData = res.data.at(0).dailyInfo;

      const _data: any = [...Array(days)].map((n, index) => {
        const day = moment(date, 'YYYYMMDD').add(index, 'days').format('YYYYMMDD');
        return {
          day,
          parentCount: rawData?.find((item: any) => item.joinDate === day)?.parentsDailyCount || 0,
          studentCount: rawData?.find((item: any) => item.joinDate === day)?.studentsDailyCount || 0,
        };
      });

      _data.push({
        day: null,
        parentCount: _data.reduce((acc: any, cur: any) => acc + cur.parentCount, 0),
        studentCount: _data.reduce((acc: any, cur: any) => acc + cur.studentCount, 0),
      });

      setData(_data);
    }
  }, [res, isLoading]);
  
  const onSearch = () => {
    if (!srtDate || !endDate) {
      return infoNotification(t('student.student.message16'));
    }
    const _days = moment(endDate).diff(srtDate, 'days') + 1;
    if (_days <= 0) {
      return infoNotification(t('statistics.web.message1'));
    }
    setDays(_days);
    setDate(srtDate);

    const campusCode = auth.hqYn === 'Y'
        ? campusCodes
        : (campusCodes.length ? campusCodes : auth.campus);

    execute(getWebMemberCount({campusCode, srtDate, endDate }));
  }

  const renderContent = () => {
    switch (content) {
      case Contents.Table:
        return <TableContainer data={data} />;
      case Contents.Chart:
        return <ChartContainer data={data.slice(0, -1)}/>;
    }
  }

  return (
    <>
    <SimpleHeader
      iconFile="icon-statistics-big.svg"
      parentMenu={t('common.menu.statistics')}
      currentMenu={t('statistics.web.title1')}
      onlyTitle={true}
    />
    <div className="common-search">
      <div className="search-area">
        <ul className="search-bx">
          <li>
            <label>{t('common.menu.campus')}</label> {/* 캠퍼스 */}
            <SelectCampusMultiChoice
              placeholder={t('statistics.eLearning.title2')}
              onChange={setCampusCodes}
              value={campusCodes}
            />
            <label>
              ({campusCodes.length ? `${campusCodes.length}${t('statistics.attendance.text1')}` : t('common.text.all')})
            </label>
          </li>
          <li>
            <label>{t('sms.list.column1')}</label> {/* 기간 */}
            <div className="date-picker__area">
              <DatePicker
                value={srtDate ? moment(srtDate) : undefined}
                onChange={e => {
                  if (e === null) setSrtDate('');
                  else setSrtDate(moment(e).format('YYYYMMDD'))
                }}
                format={['YYYY-MM-DD', 'YYYYMMDD']}
              />
            </div>
            ~
            <div className="date-picker__area">
              <DatePicker
                value={endDate ? moment(endDate) : undefined}
                onChange={e => {
                  if (e === null) setEndDate('');
                  else setEndDate(moment(e).format('YYYYMMDD'))
                }}
                format={['YYYY-MM-DD', 'YYYYMMDD']}
              />
            </div>
            <div className="comm-btn__area">
              <Button
                className="btn-basic small black"
                onClick={onSearch}
                text={t('common.button.search')}
              />
            </div>
          </li>
        </ul>
      </div>
    </div>

    <div className="content scrollbar">
      {isLoading && <Loader />}
      <ErrorResponse error={error} />
      {
        data.length
          ? (
            <>
              <div className="comm-tab__line">
                <ul>
                  <TabItem
                    currentContent={content}
                    onSetContent={setContent}
                    tabContent={Contents.Table}
                    tabLabel={t('statistics.studentEnrollMonth.tab1')}
                  />
                  <TabItem
                    currentContent={content}
                    onSetContent={setContent}
                    tabContent={Contents.Chart}
                    tabLabel={t('statistics.studentEnrollMonth.tab2')}
                  />
                </ul>
              </div>
              <div>
                {renderContent()}
              </div>
            </>
            )
          : isLoading
            ? null
            : (
              <TableEmpty text={t('statistics.appday.text1')} />
              )
      }
    </div>
    {multiCampusVisible &&
      <MultiCampusModal
        visible={multiCampusVisible}
        setVisible={setMultiCampusVisible}
        campusCodes={campusCodes}
        setCampusCodes={setCampusCodes}
      />
    }
    </>
  );
}

export default StatisticsWeb;
